import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tendersAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import AppLayout from '../components/AppLayout';
import {
  Plus, Copy, Trash2, Briefcase, MapPin, Calendar, Building2, AlertCircle, Clock,
} from 'lucide-react';

// ───────────────────────────────────────────────────────────────────────────
// Status definitions — also drive the colored chips
// ───────────────────────────────────────────────────────────────────────────
const STATUSES = {
  brouillon: { label: 'Brouillon', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.35)' },
  actif:     { label: 'Actif',     color: '#7B68EE', bg: 'rgba(123,104,238,0.15)', border: 'rgba(123,104,238,0.4)' },
  soumis:    { label: 'Soumis',    color: '#60A5FA', bg: 'rgba(96,165,250,0.14)',  border: 'rgba(96,165,250,0.4)'  },
  gagne:     { label: 'Gagné',     color: '#4ADE80', bg: 'rgba(74,222,128,0.14)',  border: 'rgba(74,222,128,0.4)'  },
  perdu:     { label: 'Perdu',     color: '#F87171', bg: 'rgba(248,113,113,0.14)', border: 'rgba(248,113,113,0.4)' },
  archive:   { label: 'Archivé',   color: '#6b7280', bg: 'rgba(107,114,128,0.10)', border: 'rgba(107,114,128,0.30)'},
};

const formatDeadline = (deadline) => {
  if (!deadline) return null;
  const now = new Date();
  const d = new Date(deadline);
  const diff = Math.floor((d - now) / 86400000);
  if (diff < 0) return { label: `Dépassée il y a ${Math.abs(diff)}j`, color: '#F87171', urgent: true };
  if (diff === 0) return { label: "Aujourd'hui", color: '#FB923C', urgent: true };
  if (diff <= 3) return { label: `Dans ${diff}j`, color: '#FB923C', urgent: true };
  if (diff <= 7) return { label: `Dans ${diff}j`, color: '#FACC15', urgent: false };
  return { label: `Dans ${diff}j`, color: 'var(--muted2)', urgent: false };
};

const Tenders = () => {
  const { user, currentOrg, orgLoading, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tenders, setTenders] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      const data = await tendersAPI.list();
      setTenders(data);
    } catch {
      toast({ title: 'Erreur', description: "Impossible de charger les appels d'offre.", variant: 'destructive' });
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentOrg) fetchData();
    else if (!authLoading) setDataLoading(false);
  }, [currentOrg, authLoading, fetchData]);

  const handleDuplicate = async (e, id) => {
    e.stopPropagation();
    try {
      const newT = await tendersAPI.duplicate(id);
      setTenders(prev => [newT, ...prev]);
      toast({ title: 'AO dupliqué', description: 'Une copie a été créée en brouillon.' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet appel d'offre ?")) return;
    try {
      await tendersAPI.delete(id);
      setTenders(prev => prev.filter(t => t.id !== id));
      toast({ title: 'AO supprimé' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const filtered = tenders.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (t.title || '').toLowerCase().includes(q) ||
      (t.client || '').toLowerCase().includes(q) ||
      (t.sector || '').toLowerCase().includes(q)
    );
  });

  if (authLoading || orgLoading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`@keyframes sd-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 40, height: 40, borderRadius: '50%', border: '2.5px solid var(--border)', borderTopColor: 'var(--vio)', animation: 'sd-spin 1s linear infinite' }} />
      </div>
    );
  }

  // Status filter tabs
  const allStatusKeys = ['all', ...Object.keys(STATUSES)];
  const statusLabels = { all: 'Tous', ...Object.fromEntries(Object.entries(STATUSES).map(([k, v]) => [k, v.label])) };
  const statusCounts = { all: tenders.length };
  for (const k of Object.keys(STATUSES)) statusCounts[k] = tenders.filter(t => t.status === k).length;

  return (
    <AppLayout title="Appels d'offre" searchQuery={searchQuery} onSearchChange={setSearchQuery}>
      <div className="sd-section-head">
        <div>
          <div className="sd-section-title">Appels d'offre</div>
          <div className="sd-section-sub">
            {dataLoading ? '…' : `${filtered.length} AO${filtered.length !== 1 ? 's' : ''}${searchQuery ? ` sur ${tenders.length}` : ''}`}
          </div>
        </div>
        <button className="sd-btn sd-btn-primary" onClick={() => navigate('/tenders/new')}>
          <Plus size={12} /> Nouvel AO
        </button>
      </div>

      <div className="sd-toolbar">
        <div className="sd-filter-tabs" style={{ flexWrap: 'wrap' }}>
          {allStatusKeys.map(k => (
            <button
              key={k}
              className={`sd-filter-tab${statusFilter === k ? ' active' : ''}`}
              onClick={() => setStatusFilter(k)}
            >
              {statusLabels[k]}
              <span className="count">{statusCounts[k] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {dataLoading ? (
        <div className="sd-profile-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="sd-profile-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="sd-skeleton" style={{ height: 14, width: '70%' }} />
              <div className="sd-skeleton" style={{ height: 11, width: '50%' }} />
              <div className="sd-skeleton" style={{ height: 11, width: '60%' }} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="sd-lib-empty">
          <div className="sd-lib-empty-icon">
            <Briefcase size={32} color="var(--vio2)" />
          </div>
          <div className="sd-lib-empty-title">
            {searchQuery || statusFilter !== 'all' ? 'Aucun AO ne correspond aux filtres' : "Aucun appel d'offre pour le moment"}
          </div>
          <div className="sd-lib-empty-sub">
            {searchQuery || statusFilter !== 'all'
              ? "Essayez avec d'autres mots-clés ou un autre statut."
              : 'Importez votre premier AO pour démarrer le matching automatique avec vos consultants.'}
          </div>
          {!searchQuery && statusFilter === 'all' && (
            <button className="sd-btn sd-btn-primary" onClick={() => navigate('/tenders/new')} style={{ margin: '0 auto' }}>
              <Plus size={12} /> Importer un AO
            </button>
          )}
        </div>
      ) : (
        <div className="sd-profile-grid sd-stagger">
          {filtered.map(t => {
            const status = STATUSES[t.status] || STATUSES.brouillon;
            const skillsCount = Array.isArray(t.required_skills) ? t.required_skills.length : 0;
            const mustCount = (t.required_skills || []).filter(s => s.importance === 'must_have').length;
            const deadlineInfo = formatDeadline(t.deadline);
            return (
              <div
                key={t.id}
                className="sd-profile-card"
                onClick={() => navigate(`/tenders/${t.id}`)}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <div className="sd-card-header" style={{ alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="sd-profile-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.title || 'Sans titre'}
                    </div>
                    <div className="sd-profile-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Building2 size={11} /> {t.client || '—'}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    padding: '3px 8px', borderRadius: 12,
                    background: status.bg, color: status.color,
                    border: `1px solid ${status.border}`,
                    flexShrink: 0, whiteSpace: 'nowrap',
                  }}>{status.label}</span>
                </div>

                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 10,
                  margin: '4px 0 10px', fontSize: 11, color: 'var(--muted2)',
                }}>
                  {t.sector && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Briefcase size={11} /> {t.sector}
                    </span>
                  )}
                  {t.location && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={11} /> {t.location}
                    </span>
                  )}
                  {t.duration && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={11} /> {t.duration}
                    </span>
                  )}
                </div>

                {skillsCount > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--muted2)', marginBottom: 10 }}>
                    <strong style={{ color: 'var(--white)', fontWeight: 600 }}>{mustCount}</strong> obligatoire{mustCount !== 1 ? 's' : ''}
                    {' · '}
                    <strong style={{ color: 'var(--white)', fontWeight: 600 }}>{skillsCount - mustCount}</strong> souhaitable{skillsCount - mustCount !== 1 ? 's' : ''}
                  </div>
                )}

                {/* Deadline pill */}
                {deadlineInfo && (
                  <div style={{
                    fontSize: 10.5, fontWeight: 600,
                    color: deadlineInfo.color,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '4px 9px',
                    background: deadlineInfo.urgent ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.04)',
                    borderRadius: 10,
                    border: `1px solid ${deadlineInfo.urgent ? 'rgba(248,113,113,0.25)' : 'var(--border)'}`,
                    width: 'fit-content', marginBottom: 10,
                  }}>
                    {deadlineInfo.urgent ? <AlertCircle size={10} /> : <Calendar size={10} />}
                    Deadline {deadlineInfo.label}
                  </div>
                )}

                <div className="sd-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="sd-action-btn" title="Dupliquer" onClick={(e) => handleDuplicate(e, t.id)}>
                    <Copy size={12} /> Dupliquer
                  </button>
                  <button className="sd-action-btn danger" title="Supprimer" onClick={(e) => handleDelete(e, t.id)}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
};

export default Tenders;
